import React, { FC, useEffect } from 'react';
import { FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { REPORTING_CATEGORIES } from '@constants';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
import { ProductFormSchema } from './schema';

interface Props {
  errors: FieldErrors<ProductFormSchema>;
  register: UseFormRegister<ProductFormSchema>;
  getCustomFieldByName: (fieldName: string) => any;
}

const ReportingCategoryForm: FC<Props> = ({ errors, register, getCustomFieldByName }) => {
  const [reportingSubcategoryOne, setReportingSubcategoryOne] = React.useState<any>([]);
  const [reportingSubcategoryTwo, setReportingSubcategoryTwo] = React.useState([]);

  const mainCategoryValue = getCustomFieldByName('reporting_category_1')?.value;

  useEffect(() => {
    if (mainCategoryValue !== '') {
      const subcategoryOne: any = REPORTING_CATEGORIES.find((category) => category.name === mainCategoryValue);

      if (subcategoryOne?.subcategory) {
        setReportingSubcategoryOne(subcategoryOne.subcategory ?? []);

        const subcategoryOneValue = getCustomFieldByName('reporting_category_2')?.value;

        const subcategoryTwo: any = subcategoryOne?.subcategory.find((category: any) => category.name === subcategoryOneValue);

        if (subcategoryTwo?.subcategory) {
          setReportingSubcategoryTwo(subcategoryTwo?.subcategory ?? []);
        }
      }
    }
  }, [mainCategoryValue]);

  const onMainCategoryChange = (event: SelectChangeEvent) => {
    const subcategoryOne: any = REPORTING_CATEGORIES.find((category) => category.name === event.target.value);

    subcategoryOne?.subcategory ? setReportingSubcategoryOne(subcategoryOne.subcategory) : setReportingSubcategoryOne([]);

    setReportingSubcategoryTwo([]);
  };

  const onSubcategoryChange = (event: SelectChangeEvent) => {
    const subcategoryTwo: any = reportingSubcategoryOne.find((category: any) => category.name === event.target.value);

    subcategoryTwo?.subcategory ? setReportingSubcategoryTwo(subcategoryTwo?.subcategory) : setReportingSubcategoryTwo([]);
  };

  return (
    <>
      <Typography variant="h6" marginBottom={2} marginTop={3}>
        Reporting Category
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth error={!!errors.custom_fields?.reporting_category_1}>
            <InputLabel id="main-reporting-category">Main Category *</InputLabel>
            <Select
              {...register('custom_fields.reporting_category_1')}
              defaultValue={getCustomFieldByName('reporting_category_1')?.value || ''}
              labelId="main-category-select-label"
              id="main-category-select"
              label="Main Reporting Category *"
              onChange={onMainCategoryChange}
              data-testid="reporting-category-select"
            >
              {REPORTING_CATEGORIES.map((category) => (
                <MenuItem key={category.name} value={category.name} data-testid={`${category.name}-reporting-category`}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{errors.custom_fields?.reporting_category_1?.message}</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="reporting-subcategory-one">{reportingSubcategoryOne.length === 0 ? 'N/A' : 'Subcategory 1'}</InputLabel>
            <Select
              {...register('custom_fields.reporting_category_2')}
              defaultValue={getCustomFieldByName('reporting_category_2')?.value || ''}
              labelId="subcategory-one-select-label"
              id="subcategory-one-select"
              label="Subcategory One"
              onChange={onSubcategoryChange}
              disabled={reportingSubcategoryOne.length === 0}
              data-testid="reporting-sub-category-select"
            >
              {reportingSubcategoryOne.map((subcategory: any) => (
                <MenuItem key={subcategory.name} value={subcategory.name} data-testid={`${subcategory.name}-reporting-sub-category`}>
                  {subcategory.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="reporting-subcategory-two">{reportingSubcategoryTwo.length === 0 ? 'N/A' : 'Subcategory 2'}</InputLabel>
            <Select
              {...register('custom_fields.reporting_category_3')}
              defaultValue={getCustomFieldByName('reporting_category_3')?.value || ''}
              labelId="subcategory-two-select-label"
              id="subcategory-two-select"
              label="Subcategory Two"
              disabled={reportingSubcategoryTwo.length === 0}
              data-testid="reporting-sub-category-two-select"
            >
              {reportingSubcategoryTwo.map((subcategory: any) => (
                <MenuItem key={subcategory.name} value={subcategory.name} data-testid={`${subcategory.name}-reporting-sub-category-two`}>
                  {subcategory.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );
};

export default ReportingCategoryForm;
